import { Breadcrumb, BreadcrumbItem } from '@common/components/Breadcrumb'
import ConfirmationDialog from '@common/components/ConfirmationDialog/ConfirmationDialog'
import { QcTaskMetadataSchema, QcTaskTimepointSchema } from '@common/config/api/client'
import { Typography, Button, Stack, Menu, MenuItem, CircularProgress } from '@mui/material'
import { useState, MouseEvent } from 'react'
import TaskStateChip from './TaskStateChip'
import ESignDialog from '../modals/eSignDialog'
import { SPACING } from '@common/theme/spacing'
import { ROUTES } from 'src/routes'
import { generatePathWithParams } from '@common/utils/urlUtils'
import { useNavigate, useParams } from 'react-router-dom'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import { QueryPermission, ReadTaskPermission } from '@auth/permissionsEnum'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { AssignmentLateOutlined, AssignmentOutlined } from '@mui/icons-material'
import { useSignPayload } from '../../hooks/useSignPayload'
import { useRestoreSigningProcess } from '@auth/hooks/useRestoreSigningProcess'
import { useStudyAndSeriesFlush } from '../../contexts/StudyAndSeriesFlushContext'

export interface QCDetailsHeaderProps {
  breadcrumbs: BreadcrumbItem[]
  qcTask: QcTaskTimepointSchema
  metadata: QcTaskMetadataSchema
  onCreateReadTask?: () => void
}

const formatTaskDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-GB')
}

const QCDetailsHeader = ({ breadcrumbs, qcTask, metadata, onCreateReadTask }: QCDetailsHeaderProps) => {
  const [eSignDialogOpen, setESignDialogOpen] = useState(false)
  const [missingStudyDefinitionWarningOpen, setMissingStudyDefinitionWarningOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [isFlushingStudyAndSeries, setIsFlushingStudyAndSeries] = useState(false)
  const menuOpen = Boolean(menuAnchor)

  const qcSignMutation = useSignPayload()
  const { isSigningInProgress } = useRestoreSigningProcess(qcSignMutation, {
    successMessage: 'QC task completed successfully',
    errorMessage: 'Failed to complete QC task signing',
  })
  const { flushAllStudyAndSeriesDefinitions, hasRegisteredStudyDefinitions, scrollToStudyAndSeriesSection } =
    useStudyAndSeriesFlush()

  const { studyId, qcTaskId } = useParams<{ studyId: string; qcTaskId: string }>()
  const navigate = useNavigate()
  const { hasPermission } = useCombinedPermissions()

  const hasQueryPermission = hasPermission(QueryPermission.CREATE)
  const hasReadTaskPermission = hasPermission(ReadTaskPermission.CREATE)
  const showCreateMenu = hasQueryPermission || hasReadTaskPermission

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleCreateQuery = () => {
    handleMenuClose()
    if (studyId && qcTaskId) {
      const queriesPath = generatePathWithParams(ROUTES.QC_TASK_QUERIES.path, { studyId, qcTaskId })
      navigate(queriesPath, {
        state: { openCreateQueryDrawer: true },
      })
    }
  }

  const handleCreateReadTask = () => {
    handleMenuClose()
    if (onCreateReadTask) {
      onCreateReadTask()
    }
  }

  const proceedWithCompleteQC = async () => {
    setIsFlushingStudyAndSeries(true)
    try {
      const didFlush = await flushAllStudyAndSeriesDefinitions()
      if (didFlush) {
        setESignDialogOpen(true)
      }
    } finally {
      setIsFlushingStudyAndSeries(false)
    }
  }

  const handleCompleteQC = async () => {
    const hasStudyAndSeriesDefinition =
      (qcTask.task_submission?.exam_descriptions?.length ?? 0) > 0 || hasRegisteredStudyDefinitions()

    if (!hasStudyAndSeriesDefinition) {
      setMissingStudyDefinitionWarningOpen(true)
      return
    }
    await proceedWithCompleteQC()
  }

  const handleContinueWithoutStudyDefinition = () => {
    setMissingStudyDefinitionWarningOpen(false)
    void proceedWithCompleteQC()
  }

  const handleCloseMissingStudyDefinitionWarning = () => {
    setMissingStudyDefinitionWarningOpen(false)
    scrollToStudyAndSeriesSection()
  }

  return (
    <>
      <Stack sx={{ mb: SPACING.headerSpacingY }} direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack>
          <Breadcrumb sx={{ mb: SPACING.breadcrumbsMarginBottom }} items={breadcrumbs} />
          <Stack spacing={SPACING.spacingXxl}>
            <Stack spacing={SPACING.spacingSm}>
              <Stack direction="row" spacing={SPACING.spacingLg} alignItems="center">
                <Typography variant="h4">QC {qcTask.order}</Typography>
                <TaskStateChip taskState={qcTask.task_state} />
              </Stack>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Created: {formatTaskDate(qcTask.create_date)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" gap={SPACING.buttonSpacing}>
          {showCreateMenu && (
            <>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleMenuOpen}
                endIcon={<KeyboardArrowDownIcon />}
                aria-controls={menuOpen ? 'create-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
              >
                Create
              </Button>
              <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {hasQueryPermission && (
                  <MenuItem onClick={handleCreateQuery}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AssignmentLateOutlined fontSize="small" />
                      <Typography>New query</Typography>
                    </Stack>
                  </MenuItem>
                )}
                {hasReadTaskPermission && (
                  <MenuItem onClick={handleCreateReadTask}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AssignmentOutlined fontSize="small" />
                      <Typography>Read task</Typography>
                    </Stack>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
          {qcTask.task_state !== 'COMPLETE' && qcTask.task_state !== 'QC_FAILED' && (
            <Button
              variant="contained"
              onClick={() => void handleCompleteQC()}
              startIcon={
                isSigningInProgress || isFlushingStudyAndSeries ? <CircularProgress size={20} color="inherit" /> : null
              }
              disabled={
                metadata.submitted_form === null ||
                metadata.submitted_form === undefined ||
                isSigningInProgress ||
                isFlushingStudyAndSeries
              }
            >
              {isFlushingStudyAndSeries
                ? 'Saving...'
                : isSigningInProgress
                  ? 'Signing...'
                  : `Complete QC ${qcTask.order}`}
            </Button>
          )}
        </Stack>
      </Stack>
      {eSignDialogOpen && (
        <ESignDialog
          qcTaskId={qcTask.task_id}
          payload={{
            type: 'qc_task_submission',
          }}
          onClose={() => setESignDialogOpen(false)}
        />
      )}
      <ConfirmationDialog
        open={missingStudyDefinitionWarningOpen}
        title="Missing study and series definition"
        description="Add a study and series definition (review data bucket), or continue without one."
        confirmLabel="Continue"
        cancelLabel="Close"
        onConfirm={handleContinueWithoutStudyDefinition}
        onCancel={handleCloseMissingStudyDefinitionWarning}
      />
    </>
  )
}

export default QCDetailsHeader
